import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { AuthLayout } from './AuthLayout';
import { SignInForm } from '../forms/SignInForm';
import { SignUpForm } from '../forms/SignUpForm';

export const LoginForm: React.FC = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { signIn, signUp, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      console.log('LoginForm: User already logged in, redirecting to appropriate dashboard');
      console.log('LoginForm: User role:', user.role, 'User email:', user.email);
      
      // Special case for admin@aicrm.com
      if (user.email === 'admin@aicrm.com') {
        navigate('/dashboard', { replace: true });
        return;
      }
      
      const redirectPath = user.role === 'admin' ? '/dashboard' : '/client-portal';
      navigate(redirectPath, { replace: true });
    }
  }, [user, navigate]);

  const handleSignIn = async (email: string, password: string) => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!email.trim()) {
        setError('Email is required');
        setIsLoading(false);
        return;
      }

      if (!password.trim()) {
        setError('Password is required');
        setIsLoading(false);
        return;
      }

      const { user, error: signInError } = await signIn(email, password);
      
      if (signInError) {
        if (signInError.message.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please check your credentials and try again. For demo accounts, use password: demo123456');
        } else if (signInError.message.includes('Email not confirmed')) {
          setError('Please check your email and confirm your account before signing in.');
        } else {
          setError('Sign in failed. Please try again.');
        }
      } else if (user) {
        setSuccess('Signed in successfully!');
        console.log('LoginForm: Sign in successful, user role:', user.role, 'User email:', user.email);
        
        // Special case for admin@aicrm.com
        if (email === 'admin@aicrm.com') {
          navigate('/dashboard', { replace: true });
          return;
        }
        
        // Explicitly navigate based on role
        const redirectPath = user.role === 'admin' ? '/dashboard' : '/client-portal';
        navigate(redirectPath, { replace: true });
      }
    } catch (err: any) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Auth error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (email: string, password: string, userData: { full_name: string; role: 'admin' | 'client' }) => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!userData.full_name.trim()) {
        setError('Full name is required');
        setIsLoading(false);
        return;
      }

      if (password.length < 6) {
        setError('Password must be at least 6 characters long');
        setIsLoading(false);
        return;
      }
      
      // Special case for admin@aicrm.com
      if (email === 'admin@aicrm.com') {
        userData.role = 'admin';
      }

      console.log('LoginForm: Signing up with role:', userData.role);
      const { user, error: signUpError } = await signUp(email, password, userData);
      
      if (signUpError) {
        if (signUpError.message.includes('User already registered')) {
          setError('An account with this email already exists. Please sign in instead or use a different email address.');
          // Auto-switch to sign in form for better UX
          setTimeout(() => {
            setIsSignUp(false);
            setError('Account already exists. Please sign in with your credentials.');
          }, 2000);
        } else if (signUpError.message.includes('Invalid email')) {
          setError('Please enter a valid email address.');
        } else {
          setError(signUpError.message);
        }
      } else if (user) {
        setSuccess('Account created successfully! You can now sign in with your credentials.');
        setTimeout(() => {
          setIsSignUp(false);
          setSuccess('Account created! Please sign in now.');
        }, 2000);
      }
    } catch (err: any) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Auth error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = () => {
    setIsSignUp(!isSignUp);
    setError('');
    setSuccess('');
  };

  return (
    <AuthLayout
      title={isSignUp ? 'Create Account' : 'Welcome Back'}
      subtitle={isSignUp ? 'Sign up for your AI-powered CRM' : 'Sign in to your AI-powered CRM'}
    >
      {isSignUp ? (
        <SignUpForm
          onSubmit={handleSignUp}
          loading={isLoading}
          error={error}
          success={success}
          onSwitchToSignIn={switchMode}
        />
      ) : (
        <SignInForm
          onSubmit={handleSignIn}
          loading={isLoading}
          error={error}
          success={success}
          onSwitchToSignUp={switchMode}
        />
      )}
    </AuthLayout>
  );
};